import React, { useState, useEffect } from 'react';
import { Handle, Position, useNodes } from '@xyflow/react';
import { 
  Card, 
  Space, 
  Input, 
  Button, 
  Select, 
  Typography, 
  Tabs, 
  Popconfirm, 
  Tooltip,
  Switch,
  Divider
} from 'antd';
import { 
  DeleteOutlined, 
  FileOutlined,
  FileTextOutlined,
  FileWordOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  UploadOutlined,
  LinkOutlined
} from '@ant-design/icons';
import GenericNodeContent from '../common/GenericNodeContent';

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

const CreateFileNode = ({ id, data, isConnectable }) => {
  // State management
  const [nodeName, setNodeName] = useState(data.nodeName || `Create File ${id}`);
  const [fileName, setFileName] = useState(data.fileName || '');
  const [fileFormat, setFileFormat] = useState(data.fileFormat || 'txt');
  const [fileContent, setFileContent] = useState(data.fileContent || '');
  const [activeTab, setActiveTab] = useState('content');
  const [saveToMedia, setSaveToMedia] = useState(data.saveToMedia !== false); // Default to true
  const [previewVisible, setPreviewVisible] = useState(false);
  
  const nodes = useNodes();

  // Update parent data when state changes
  useEffect(() => {
    data.updateNodeData(id, {
      nodeName,
      fileName,
      fileFormat,
      fileContent,
      saveToMedia,
      handleContentChange,
      handleFileNameChange
    });
  }, [nodeName, fileName, fileFormat, fileContent, saveToMedia, id, data]);

  // Handler functions
  const handleNameChange = (e) => {
    setNodeName(e.target.value);
  };

  const handleFileNameChange = (nodeId, newData) => {
    setFileName(newData.content);
    data.updateNodeData(nodeId, { fileName: newData.content });
  };

  const handleFileFormatChange = (value) => {
    setFileFormat(value);
  };

  const handleContentChange = (nodeId, newData) => {
    setFileContent(newData.content);
    data.updateNodeData(nodeId, { fileContent: newData.content });
  };

  const handleSaveToMediaChange = (checked) => {
    setSaveToMedia(checked);
  };

  const handlePreviewToggle = () => {
    setPreviewVisible(!previewVisible);
  };

  // Render file format icon
  const getFileFormatIcon = () => {
    switch (fileFormat) {
      case 'txt':
        return <FileTextOutlined />;
      case 'docx':
        return <FileWordOutlined />;
      case 'html':
        return <FileWordOutlined />;
      default:
        return <FileOutlined />;
    }
  };

  // Simple preview panel
  const renderPreviewPanel = () => {
    if (!previewVisible) return null;
    
    return (
      <div 
        style={{ 
          border: '1px solid #d9d9d9', 
          padding: 16, 
          marginTop: 16, 
          borderRadius: 8,
          backgroundColor: '#f9f9f9'
        }}
      >
        <Text strong>Preview: {fileName || 'Untitled'}.{fileFormat}</Text>
        <Divider style={{ margin: '8px 0' }} />
        <div style={{ 
          whiteSpace: 'pre-wrap',
          fontFamily: fileFormat === 'txt' ? 'monospace' : 'inherit',
          minHeight: 100,
          maxHeight: 200,
          overflow: 'auto',
          padding: 8,
          backgroundColor: '#fff',
          border: '1px solid #eee',
          borderRadius: 4
        }}>
          {fileContent || <Text type="secondary">No content yet</Text>}
        </div>
      </div>
    );
  };

  return (
    <Card
      title={
        <Space>
          <FileOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
          <span>Create File</span>
          <Text type="secondary">.{fileFormat}</Text>
        </Space>
      }
      style={{ width: 380 }}
      extra={
        <Space>
          <Tooltip title="Preview">
            <Button
              icon={<EyeOutlined />}
              type="text"
              onClick={handlePreviewToggle}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Node"
            description="Are you sure you want to delete this node?"
            onConfirm={() => data.onDelete(id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      }
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />

      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={handleNameChange}
        style={{ marginBottom: 16 }}
        prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
        className="nodrag"
      />

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Content" key="content">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space align="center">
              <Text strong>File Content</Text>
              <Tooltip title="Enter the content for your file. You can use tags from previous nodes.">
                <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
              </Tooltip>
            </Space>
            
            <GenericNodeContent
              id={id}
              data={{ content: fileContent }}
              onChange={handleContentChange}
              placeholder="Enter your file content here..."
              nodes={nodes}
            />
          </Space>
        </TabPane>

        <TabPane tab="File Settings" key="fileSettings">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ marginBottom: 16 }}>
              <Space align="center">
                <Text strong>File Name</Text>
                <Tooltip title="Enter a name for your file. You can use dynamic tags.">
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              </Space>
              
              <div style={{ marginTop: 4 }}>
                <GenericNodeContent
                  id={id}
                  data={{ content: fileName }}
                  onChange={handleFileNameChange}
                  placeholder="Enter file name"
                  nodes={nodes}
                  style={{ marginBottom: 4 }}
                />
              </div>
              <Text type="secondary">
                Example: report-[Input from trigger-1]
              </Text>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>File Format</Text>
              <Select
                value={fileFormat}
                onChange={handleFileFormatChange}
                style={{ width: '100%', marginTop: 4 }}
                className="nodrag"
              >
                <Option value="txt">
                  <Space>
                    <FileTextOutlined />
                    <span>Plain Text (.txt)</span>
                  </Space>
                </Option>
                <Option value="docx">
                  <Space>
                    <FileWordOutlined />
                    <span>Word Document (.docx)</span>
                  </Space>
                </Option>
                <Option value="html">
                  <Space>
                    <FileWordOutlined />
                    <span>HTML Document (.html)</span>
                  </Space>
                </Option>
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Space align="center">
                <Text strong>Save to Media Library</Text>
                <Tooltip title="Add this file to your WordPress Media Library for easy access">
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              </Space>
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={saveToMedia}
                  onChange={handleSaveToMediaChange}
                  checkedChildren={<UploadOutlined />}
                />
                <Text style={{ marginLeft: 8 }}>
                  {saveToMedia ? 'File will be added to Media Library' : 'File will only be saved in uploads folder'}
                </Text>
              </div>
            </div>
            
          </Space>
        </TabPane>
      </Tabs>

      {renderPreviewPanel()}

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
  );
};

export default CreateFileNode;